// src/ui/screens/settings/ContactUsScreen.jsx

import React from 'react';
import { useNavigate } from 'react-router-dom';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── Contact Button ──────────────────────────────────────────────────────────
//
// `variant="row"` (default) is the original mobile layout: horizontal,
// icon + title/subtitle + chevron. `variant="card"` is a centered vertical
// layout used by the desktop grid — same icon-path logic, no duplication.

const ContactButton = ({ title, subtitle, icon, iconColor, onTap, variant = 'row' }) => {
  const getIconPath = () => {
    switch (icon) {
      case 'whatsapp':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        );
      case 'email':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        );
      case 'call':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        );
      default:
        return null;
    }
  };

  if (variant === 'card') {
    return (
      <button
        onClick={onTap}
        className="w-full p-6 rounded-2xl flex flex-col items-center text-center gap-3 border border-white/10 hover:border-white/20 hover:bg-white/[0.03] transition-all"
        style={{ backgroundColor: COLORS.cardLight }}
      >
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center border"
          style={{
            backgroundColor: `${iconColor}26`,
            borderColor: `${iconColor}4D`,
          }}
        >
          <svg className="w-7 h-7" fill="none" stroke={iconColor} viewBox="0 0 24 24">
            {getIconPath()}
          </svg>
        </div>
        <div>
          <p className="text-white text-base font-bold">{title}</p>
          <p className="text-white/60 text-xs font-medium mt-0.5">{subtitle}</p>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onTap}
      className="w-full p-4 rounded-xl flex items-center gap-4 border border-white/10"
      style={{ backgroundColor: COLORS.cardLight }}
    >
      <div
        className="w-[54px] h-[54px] rounded-xl flex items-center justify-center border"
        style={{
          backgroundColor: `${iconColor}26`,
          borderColor: `${iconColor}4D`,
        }}
      >
        <svg className="w-7 h-7" fill="none" stroke={iconColor} viewBox="0 0 24 24">
          {getIconPath()}
        </svg>
      </div>
      <div className="flex-1 text-left">
        <p className="text-white text-lg font-bold">{title}</p>
        <p className="text-white/60 text-xs font-medium">{subtitle}</p>
      </div>
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
      >
        <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
};

// ── ContactUsScreen ─────────────────────────────────────────────────────────

export const ContactUsScreen = () => {
  const navigate = useNavigate();

  const PHONE_NUMBER = '+256766697924';
  const EMAIL = 'filatomapp@gmail.com';

  const handleBack = () => {
    navigate(-1);
  };

  const handleLaunchWhatsApp = () => {
    window.open(`https://wa.me/${PHONE_NUMBER}`, '_blank');
  };

  const handleLaunchEmail = () => {
    window.location.href = `mailto:${EMAIL}?subject=Support Request&body=Hello Filatom Support,%0A%0A`;
  };

  const handleLaunchPhone = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Top Bar */}
        <div className="pt-safe-top px-3 pb-1">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
              style={{ backgroundColor: COLORS.card }}
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-white text-xl font-bold">Contact Us</span>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-8">
          <div className="mt-2 text-center">
            <p className="text-white text-sm font-bold tracking-wide">
              REACH FILATOM SUPPORT FOR HELP
            </p>
          </div>

          <div className="mt-6 p-5 rounded-xl space-y-4" style={{ backgroundColor: COLORS.card }}>
            <ContactButton
              title="WhatsApp"
              subtitle="24/7, Fastest Support"
              icon="whatsapp"
              iconColor="#25D366"
              onTap={handleLaunchWhatsApp}
            />

            <ContactButton
              title="Email"
              subtitle="Write to Us"
              icon="email"
              iconColor="#EA4335"
              onTap={handleLaunchEmail}
            />

            <ContactButton
              title="Call"
              subtitle="Speak to Our Agent"
              icon="call"
              iconColor="#34A853"
              onTap={handleLaunchPhone}
            />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up.
          Sticky header, then the three contact options as a centered
          three-up card grid instead of a stacked list.
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="sticky top-0 z-10 backdrop-blur-sm border-b border-white/10" style={{ backgroundColor: `${COLORS.bg}E6` }}>
          <div className="max-w-3xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
              style={{ backgroundColor: COLORS.card }}
              aria-label="Go back"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-white text-2xl font-bold leading-tight">Contact Us</h1>
              <p className="text-white/40 text-sm mt-0.5">Reach Filatom support for help</p>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-8 py-12">
          <div className="grid grid-cols-3 gap-4">
            <ContactButton
              title="WhatsApp"
              subtitle="24/7, Fastest Support"
              icon="whatsapp"
              iconColor="#25D366"
              onTap={handleLaunchWhatsApp}
              variant="card"
            />

            <ContactButton
              title="Email"
              subtitle="Write to Us"
              icon="email"
              iconColor="#EA4335"
              onTap={handleLaunchEmail}
              variant="card"
            />

            <ContactButton
              title="Call"
              subtitle="Speak to Our Agent"
              icon="call"
              iconColor="#34A853"
              onTap={handleLaunchPhone}
              variant="card"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsScreen;